import { Box, Typography } from '@mui/material'
import IconDropdown from 'components/IconDropdown/IconDropdown'
import { TooltipText } from 'components/TooltipText/TooltipText'
import { useFormikContext } from 'formik'
import { t } from 'i18next'
import theme from 'lib/theme'
import { concat } from 'lodash'
import { useRoutePlanning } from 'providers/RoutePlanningProvider'
import { useState, SyntheticEvent } from 'react'
import { ORDER_STATUS } from 'types/api/order'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import { Dialog } from 'components/Dialog/Dialog'
import { RoutePlanningFormProps } from './OrderListing'
import { StatusRoundIcon } from './elements'
import { listOfDeliveryNames } from './utils'

type ListItemProps = {
  color: string
  routeName: string
  routeNameIndex: number
  partId: string
  endTime: string
  startTime: string
  status: ORDER_STATUS
  deliveryId: number
  visibleId: string
}

export const ListItem = ({
  color,
  routeName,
  routeNameIndex,
  partId,
  endTime,
  startTime,
  status,
  deliveryId,
  visibleId,
}: ListItemProps) => {
  const { deliveries } = useRoutePlanning()
  const [warning, setWarning] = useState(false)
  const [type, setType] = useState<string | null>(null)

  const { setFieldValue, handleSubmit } =
    useFormikContext<RoutePlanningFormProps>()

  const handleDropdownAction = (type: string) => {
    setFieldValue('actionType', 'single_order_action')
    setFieldValue('submitType', type)
    setFieldValue('orders', [{ orderId: partId, deliveryId }])
    handleSubmit()
  }

  const handleDropDownButtonClick = (type: string) => {
    if (status === 'delivering') {
      setType(type)
      setWarning(true)
    } else {
      handleDropdownAction(type)
    }
  }

  const getOptions = () => {
    const initialOptions = [
      {
        value: 'createShipment',
        label: t('routePlanning.createNewRoute'),
      },
      ...listOfDeliveryNames(deliveries, [routeName]),
    ]

    const removeFromRouteOption = {
      value: `removeFromRoute/${partId}/${deliveryId}/${routeName}`,
      label: t('routePlanning.removeFromRoute'),
    }

    if (status === ORDER_STATUS.CANCELLED) {
      return [removeFromRouteOption]
    }
    return !/^-?\d+$/.test(routeName)
      ? concat(initialOptions, removeFromRouteOption)
      : initialOptions
  }

  const handleCloseAlert = (
    event?: SyntheticEvent | Event,
    reason?: string
  ) => {
    if (reason === 'clickaway') {
      return
    }

    setWarning(false)
  }

  return (
    <Box
      display='flex'
      alignItems='center'
      flexDirection='row'
      justifyContent='space-between'
      width='100%'>
      <Dialog
        dialogOpen={warning}
        handleConfirm={() => handleDropdownAction(type as string)}
        title={t('editOrderRoute')}
        text={t('editOrderText')}
        handleClose={handleCloseAlert}
        cancelText={t('cancel')}
        confirmText={t('confirm')}
      />
      <Box display='flex' alignItems='flex-end'>
        <Box mr={2}>
          <StatusRoundIcon oval color={color} data-cy='order route name'>
            {color !== theme.palette.text.primary
              ? routeName + routeNameIndex
              : routeName}
          </StatusRoundIcon>
          <Typography variant='body2'>
            {startTime} - {endTime}
          </Typography>
        </Box>
        <Box mr={3}>
          <TooltipText
            text={
              <>
                <Typography>{t('orderInfo.partId', { partId })}</Typography>
                <Typography>
                  {t('orderInfo.orderNumber', { visibleId })}
                </Typography>
              </>
            }>
            <Typography
              variant='body2'
              color='text.secondary'
              data-cy='order part id'>
              #{visibleId}
            </Typography>
          </TooltipText>
        </Box>
        <Typography variant='body1' color='text.secondary'>
          {t(`orderStatus.${status}`)}
        </Typography>
      </Box>
      {status !== ORDER_STATUS.DELIVERED ? (
        <Box>
          <IconDropdown
            height='222px'
            isIconOnly
            ariaLabel={t('routePlanning.actionMenu')}
            onChange={(_, value) => handleDropDownButtonClick(value)}
            options={getOptions()}
            Icon={MoreVertIcon}
          />
        </Box>
      ) : null}
    </Box>
  )
}
